import { request } from './client';

/**
 * Tenant-scoped clinical write paths — ABHA, Care Context, Consent.
 *
 * URLs and field names follow the published M1 Connector API
 * Documentation v2.0 (25 Apr 2026). Every call goes through the
 * live-token + X-Tenant-Id axios interceptor, but the documented
 * endpoints also expect `tenantId` in the JSON body — so callers pass
 * it in explicitly. The portal already has the tenant from the JWT
 * (`claims.tenantId`), so it's wired in at the AbhaPage layer rather
 * than buried here.
 *
 * Naming gotchas baked into the doc that we honour verbatim:
 *   - request/otp uses `value` (NOT `loginId`) and `type` (NOT `loginHint`)
 *   - v3 verify endpoints use `otpValue`; profile/login/verify uses `otp`
 *   - profile/account & getAbha endpoints use `XToken` (capital X+T)
 */

// =================================================================
// ABHA — Aadhaar OTP wizard + profile fetch
// =================================================================

export interface OtpRequestResponse {
  /** Carries through the wizard. Sent back as txnId on every subsequent call. */
  txnId: string;
  /** Optional masked mobile to surface to the user. */
  mobileNumber?: string;
  message?: string;
}

export interface AbhaProfile {
  abhaNumber?: string;
  abhaAddress?: string;
  name?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: string;
  yearOfBirth?: number;
  dayOfBirth?: number;
  monthOfBirth?: number;
  /** Constructed YYYY-MM-DD from ABDM dayOfBirth/monthOfBirth/yearOfBirth. */
  dob?: string;
  mobile?: string;
  email?: string;
  address?: string;
  pinCode?: string;
  pincode?: string;
  state?: string;
  district?: string;
  /** PNG/JPEG card art base64 (only present on the card endpoint). */
  cardBase64?: string | null;
}

export interface CheckRevisitResponse {
  isExistingPatient: boolean;
  hasDifferences: boolean;
  existingData?: Record<string, unknown>;
  differences?: Record<string, { existing: string; current: string }>;
  uhidNumber?: string;
  message?: string;
}

export interface SuggestionsResponse {
  txnId: string;
  abhaAddressList?: string[];
  /** v1's API uses `suggestions`; v2 uses `abhaAddressList`. We accept either. */
  suggestions?: string[];
  message?: string;
}

export const abha = {
  // ── 2.1 Request Aadhaar / Mobile OTP ─────────────────────────────
  requestOtp: (body: { tenantId: string; type: 'aadhaar' | 'mobile'; value: string; txnId?: string }) =>
    request<{ status?: boolean; message?: string; data?: OtpRequestResponse }>({
      url: 'api/abdm/v3/request/otp',
      method: 'POST',
      data: { txnId: '', ...body },
    }),

  // ── 2.2 Verify Aadhaar OTP ──────────────────────────────────────
  // `mobile` is optional — when omitted ABDM falls back to the
  // Aadhaar-linked number for future notifications.
  verifyAadhaarOtp: (body: { tenantId: string; txnId: string; otpValue: string; mobile?: string }) =>
    request<{ status?: boolean; message?: string; data?: OtpRequestResponse & { ABHAProfile?: AbhaProfile } }>({
      url: 'api/abdm/v3/verifyaadhaar/otp',
      method: 'POST',
      data: body,
    }),

  // ── 2.3 Verify Mobile OTP (only when mobile differs from Aadhaar-linked) ─
  verifyMobileOtp: (body: { tenantId: string; txnId: string; otpValue: string }) =>
    request<{ status?: boolean; message?: string; data?: OtpRequestResponse }>({
      url: 'api/abdm/v3/verifymobile/otp',
      method: 'POST',
      data: body,
    }),

  // ── 2.4 ABHA-address suggestions ────────────────────────────────
  suggestions: (body: { tenantId: string; txnId: string }) =>
    request<{ status?: boolean; data?: SuggestionsResponse }>({
      url: 'api/abdm/getAbha/suggestions',
      method: 'POST',
      data: body,
    }),

  // ── 2.5 Create ABHA Address ─────────────────────────────────────
  createAbhaAddress: (body: {
    tenantId: string;
    txnId: string;
    abhaAddress: string;
    /** "1" for preferred, "0" otherwise. */
    preferred?: '0' | '1';
  }) =>
    request<{ status?: boolean; message?: string; data?: { XToken?: string; xToken?: string } & AbhaProfile }>({
      url: 'api/abdm/create/abha/address',
      method: 'POST',
      data: { preferred: '0', ...body },
    }),

  // ── 2.6 Fetch ABHA Profile (after enrollment) ───────────────────
  fetchProfileByXToken: (body: { tenantId: string; XToken: string }) =>
    request<{ status?: boolean; data?: AbhaProfile }>({
      url: 'api/abdm/getAbha/profile/details',
      method: 'GET',
      data: body,
    }),

  // ── 2.7 Fetch ABHA Card (after enrollment) ──────────────────────
  fetchCardByXToken: (body: { tenantId: string; XToken: string }) =>
    request<{ status?: string; data?: string; message?: string }>({
      url: 'api/abdm/getAbha/card',
      method: 'POST',
      data: body,
    }),

  // ── 4.1 Search ABHA by PHR address ──────────────────────────────
  searchByAddress: (body: { tenantId: string; abhaAddress: string }) =>
    request<{
      status?: boolean;
      data?: {
        abhaAddress?: string;
        name?: string;
        authMethods?: ('AADHAAR_OTP' | 'MOBILE_OTP')[];
        healthIdNumber?: string;
      };
    }>({
      url: 'api/abdm/phr/web/login/abha/search',
      method: 'POST',
      data: body,
    }),

  // ── 3.1 Profile login OTP request (standalone mobile / ABHA-number login) ──
  profileLoginRequestOtp: (body: { tenantId: string; type: 'communication-mobile' | 'aadhaar' | 'mobile'; value: string; txnId?: string }) =>
    request<{ status?: boolean; message?: string; data?: OtpRequestResponse }>({
      url: 'api/abdm/profile/login/request/otp',
      method: 'POST',
      data: { txnId: '', ...body },
    }),

  // ── 3.2 Profile login verify OTP ────────────────────────────────
  // ABDM returns a T-Token (typ:Transfer) + accounts list, not a session token.
  // Call profileLoginVerifyUser next to exchange T-Token for session token.
  profileLoginVerify: (body: { tenantId: string; txnId: string; otp: string; type?: string }) =>
    request<{
      status?: boolean; message?: string;
      data?: {
        token?: string;   // T-Token (ABDM may use `token` or `t_token`)
        t_token?: string;
        txnId?: string;
        expiresIn?: number;
        accounts?: { ABHANumber?: string; name?: string; preferredAbhaAddress?: string }[];
      };
    }>({
      url: 'api/abdm/profile/login/verify',
      method: 'POST',
      data: body,
    }),

  // ── 3.3 Select account after mobile OTP (exchange T-Token for session token) ──
  profileLoginVerifyUser: (body: { tenantId: string; ABHANumber: string; txnId?: string; tToken: string }) =>
    request<{ status?: boolean; message?: string; data?: { token?: string; expiresIn?: number } }>({
      url: 'api/abdm/profile/login/verify/user',
      method: 'POST',
      data: body,
    }),

  // ── 3.4 Fetch ABHA Profile by session XToken ──────────────────────
  profileAccount: (body: { tenantId: string; XToken: string }) =>
    request<{ status?: boolean; message?: string; data?: AbhaProfile }>({
      url: 'api/abdm/profile/account',
      method: 'POST',
      data: body,
    }),

  // ── 4.4 Fetch PHR ABHA Profile by XToken (after phr/web/login/abha/verify) ──
  phrAbhaProfile: (body: { tenantId: string; XToken: string }) =>
    request<{ status?: boolean; message?: string; data?: AbhaProfile }>({
      url: 'api/abdm/phr/web/login/profile/abha-profile',
      method: 'POST',
      data: body,
    }),

  // ── 4.2 / 4.3 PHR-address OTP request + verify ──────────────────
  phrRequestOtp: (body: { tenantId: string; type: 'aadhaar' | 'mobile'; value: string; txnId?: string }) =>
    request<{ status?: boolean; data?: OtpRequestResponse }>({
      url: 'api/abdm/phr/web/login/abha/request/otp',
      method: 'POST',
      data: { txnId: '', ...body },
    }),
  phrVerifyOtp: (body: { tenantId: string; txnId: string; type: 'aadhaar' | 'mobile'; otp: string }) =>
    request<{ status?: boolean; message?: string; data?: { token?: string; XToken?: string; expiresIn?: number } }>({
      url: 'api/abdm/phr/web/login/abha/verify',
      method: 'POST',
      data: body,
    }),

  // ── 5.4 List patients (paginated) ───────────────────────────────
  // Backend returns PaginatedResponseDto<MemberResponseDto>:
  //   { data: [...], totalCount: N, page: N, size: N }
  // MemberResponseDto uses abdmHealthId (not abhaNumber) and abhaAddress as string[].
  listPatients: (params: { tenantId: string; page?: number; size?: number; user?: string }) =>
    request<{
      data?: Array<PatientProfile & { abdmHealthId?: string; abhaAddress?: string | string[] }>;
      totalCount?: number;
      page?: number;
      size?: number;
    }>({
      url: 'api/abdm/patient-profile-get-all',
      method: 'GET',
      params,
    }),
  // ── 5.5 Get one patient by id ───────────────────────────────────
  getPatient: (params: { tenantId: string; id: string }) =>
    request<{ status?: boolean; data?: PatientProfile }>({
      url: 'api/abdm/patient-profile',
      method: 'GET',
      params,
    }),

  // ── 5.6 Register / upsert patient after M1 ──────────────────────
  savePatient: (body: {
    tenantId: string;
    abhaAddress?: string;
    abdmHealthId?: string;
    mobile?: string;
    yearOfBirth?: number;
    dob?: string;
    gender?: string;
    person?: { name?: string };
    address?: string;
    pincode?: string;
    district?: string;
    state?: string;
  }) =>
    request<PatientProfile>({ url: 'api/abdm/patient-profile', method: 'POST', data: body }),

  // ── 5.7 Update existing patient profile ─────────────────────────
  updateProfile: (body: {
    id?: string;
    tenantId: string;
    abhaAddress?: string;
    abdmHealthId?: string;
    uhidNumber?: string;
    mobile?: string;
    yearOfBirth?: number;
    dob?: string;
    gender?: string;
    person?: { name?: string };
    address?: string;
    pincode?: string;
    district?: string;
    state?: string;
    memberNumber?: string;
  }) =>
    request<PatientProfile>({ url: 'api/abdm/update-profile', method: 'PUT', data: body }),

  // ── 5.8 Check if patient is a revisit (call before savePatient) ─
  checkRevisit: (body: {
    tenantId: string;
    abhaAddress?: string;
    abdmHealthId?: string;
    mobile?: string;
    yearOfBirth?: number;
    dob?: string;
    gender?: string;
    person?: { name?: string };
    address?: string;
    pincode?: string;
    district?: string;
    state?: string;
  }) =>
    request<CheckRevisitResponse>({ url: 'api/abdm/patients/check-revisit', method: 'POST', data: body }),
};

// =================================================================
// M2 — Consultation push (HIP). One endpoint accepts all 8 FHIR
// bundle types (Prescription / OPConsultation / DiagnosticReport /
// HealthDocument / WellnessRecord / Immunization / DischargeSummary /
// Invoice). The `clinical_data` shape varies per fhirBundleType.
// =================================================================

export type FhirBundleType =
  | 'Prescription'
  | 'OPConsultation'
  | 'DiagnosticReport'
  | 'HealthDocumentRecord'
  | 'WellnessRecord'
  | 'Immunization'
  | 'DischargeSummary'
  | 'Invoice';

export interface ConsultationPushBody {
  tenantId: string;
  memberId: string;
  consultationNumber: string;
  status: string;
  appointmentDate: string;
  linkedToABDM: boolean;
  abhaAddress: string;
  abhaNumber: string;
  fhirBundleType: FhirBundleType;
  clinical_data: Record<string, unknown>;
}

export const consultations = {
  push: (body: ConsultationPushBody) =>
    request<{ status: string; consultationNumber: string; message?: string }>({
      url: 'api/abdm/consultations',
      method: 'POST',
      data: body,
    }),
  status: (consultationNumber: string) =>
    request<{ consultationNumber: string; status: string; errorDetails?: string; createdAt?: string; updatedAt?: string }>({
      url: `api/abdm/consultations/${encodeURIComponent(consultationNumber)}/status`,
      method: 'GET',
    }),
};

export interface PatientProfile {
  id: string;
  abhaNumber?: string;
  abhaAddress?: string;
  abdmHealthId?: string;
  name?: string;
  person?: { name?: string };
  gender?: string;
  mobile?: string;
  uhidNumber?: string;
  registered?: boolean;
  yearOfBirth?: number;
  dob?: string;
  address?: string | { line?: string };
  pincode?: string;
  district?: string;
  state?: string;
  memberNumber?: string;
  createdDate?: string;
  updatedDate?: string;
}

// =================================================================
// Care Context — discover → link init → confirm  (M2 — left as-is)
// =================================================================

export interface DiscoverRequest {
  abhaAddress: string;
  patientName?: string;
  yearOfBirth?: number;
  gender?: string;
}

export interface DiscoverResponse {
  transactionId: string;
  patient: {
    referenceNumber: string;
    display: string;
    careContexts?: { referenceNumber: string; display: string }[];
  };
}

export interface LinkInitRequest {
  transactionId: string;
  referenceNumber: string;
  careContextReferences: string[];
}

export interface LinkInitResponse {
  transactionId: string;
  requiresOtp: boolean;
}

export interface LinkConfirmRequest {
  transactionId: string;
  otp?: string;
}

export interface LinkedCareContextSummary {
  id: string;
  abhaAddress: string;
  abhaNumber?: string;
  status: string;
  careContexts: { referenceNumber: string; display: string }[];
  updatedAt?: string;
}

export const careContext = {
  list: (params: { page?: number; size?: number }) =>
    request<{
      content: LinkedCareContextSummary[];
      totalElements: number;
      totalPages: number;
      number: number;
      size: number;
      first: boolean;
      last: boolean;
      numberOfElements: number;
    }>({
      url: 'api/abdm/care-context/list',
      method: 'GET',
      params,
    }),
  discover: (body: DiscoverRequest) =>
    request<DiscoverResponse>({ url: 'api/abdm/care-context/discover', method: 'POST', data: body }),
  linkInit: (body: LinkInitRequest) =>
    request<LinkInitResponse>({ url: 'api/abdm/care-context/link/init', method: 'POST', data: body }),
  linkConfirm: (body: LinkConfirmRequest) =>
    request<{ status: string; message?: string }>({
      url: 'api/abdm/care-context/link/confirm',
      method: 'POST',
      data: body,
    }),
};

// =================================================================
// Consent — list, raise, view records (M3)
// =================================================================

export interface ConsentRaiseRequest {
  healthId: string;
  purposeText?: string;
  purpose?: string;
  hiTypes: string[];
  permissionFrom: string;
  permissionTo: string;
  expiry: string;
  hipId?: string;
}

/** Normalised consent record used throughout the UI. */
export interface ConsentRecord {
  id: string;
  consentRequestId?: string;
  consentArtefactId?: string;
  abhaAddress: string;
  abhaNumber?: string;
  status: string;
  hiTypes: string[];
  grantedhiTypes?: string[];
  purpose?: string;
  permissionFrom?: string;
  permissionTo?: string;
  expiry?: string;
  approvedFrom?: string;
  approvedTo?: string;
  approvedExpiry?: string;
  createdAt: string;
  approvedAt?: string;
}

export interface ConsentListQuery {
  healthId: string;
  tenantId: string;
  page?: number;
  size?: number;
  status?: string;
  hiTypes?: string;
}

// Raw shape returned by /api/abdm/api/consents/list
interface _RawConsentItem {
  id: string;
  consentRequestId?: string;
  patient?: { id?: string; name?: string };
  status: string;
  hiTypes?: string[];
  grantedhiTypes?: string[];
  fromDate?: string;
  toDate?: string;
  expiredDate?: string;
  approvedFromDate?: string;
  approvedToDate?: string;
  approvedExpiry?: string;
  createdDate?: string;
  approvedDate?: string;
  consentArtefactsList?: { id: string }[];
}

interface _RawConsentListResponse {
  status: string;
  abdmHiuPatientCareContextList?: {
    data: _RawConsentItem[];
    totalCount: number;
    page: number;
    size: number;
  };
}

/**
 * The backend addOffset() produces IST timestamps without a timezone marker,
 * e.g. "2026-05-13T14:07:54.975". JavaScript's Date constructor treats these
 * as local time, which is only correct when the browser is in IST (UTC+5:30).
 * Appending "+05:30" makes the intent explicit and keeps relative-time correct
 * across all timezones.
 */
function _withIstIfBare(s?: string): string {
  if (!s) return '';
  if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) return s; // already has tz
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s + '+05:30';       // bare ISO local → tag IST
  return s;
}

function _mapConsentItem(raw: _RawConsentItem): ConsentRecord {
  // Normalise status: backend may return "INITIATED" for pending requests;
  // surface it as "REQUESTED" so the UI dropdown value matches the display label.
  const status = raw.status === 'INITIATED' ? 'REQUESTED' : raw.status;
  return {
    id:                raw.id,
    consentRequestId:  raw.consentRequestId ?? raw.id,
    consentArtefactId: raw.consentArtefactsList?.[0]?.id,
    abhaAddress:       raw.patient?.id ?? '',
    status,
    hiTypes:           raw.hiTypes ?? [],
    grantedhiTypes:    raw.grantedhiTypes,
    permissionFrom:    _withIstIfBare(raw.fromDate),
    permissionTo:      _withIstIfBare(raw.toDate),
    expiry:            _withIstIfBare(raw.expiredDate),
    approvedFrom:      _withIstIfBare(raw.approvedFromDate),
    approvedTo:        _withIstIfBare(raw.approvedToDate),
    approvedExpiry:    _withIstIfBare(raw.approvedExpiry),
    createdAt:         _withIstIfBare(raw.createdDate),
    approvedAt:        _withIstIfBare(raw.approvedDate),
  };
}

export const consent = {
  list: async (q: ConsentListQuery) => {
    // Backend stores pending requests as "INITIATED"; the UI normalises to "REQUESTED".
    // Translate back so the server-side filter works correctly.
    const backendStatus = q.status === 'REQUESTED' ? 'INITIATED' : q.status;
    const raw = await request<_RawConsentListResponse>({
      url: 'api/abdm/api/consents/list',
      method: 'GET',
      params: {
        healthId: q.healthId,
        tenantId: q.tenantId,
        page:     q.page ?? 0,
        size:     q.size ?? 25,
        ...(backendStatus ? { status:  backendStatus } : {}),
        ...(q.hiTypes     ? { hiTypes: q.hiTypes     } : {}),
      },
    });
    const list = raw.abdmHiuPatientCareContextList;
    const items = (list?.data ?? []).map(_mapConsentItem);
    return {
      content:          items,
      totalElements:    list?.totalCount ?? items.length,
      totalPages:       Math.ceil((list?.totalCount ?? items.length) / (list?.size ?? 25)),
      number:           list?.page ?? 0,
      size:             list?.size ?? 25,
      first:            (list?.page ?? 0) === 0,
      last:             (list?.page ?? 0) + 1 >= Math.ceil((list?.totalCount ?? items.length) / (list?.size ?? 25)),
      numberOfElements: items.length,
    };
  },

  raise: (body: ConsentRaiseRequest) =>
    request<{ abhaAddress: string; requestId: string; consentRequestId: string | null; message: string }>({
      url: 'api/abdm/api/v3/frontend/consent/request',
      method: 'POST',
      data: {
        consent: {
          purpose: {
            code: body.purpose ?? 'CAREMGT',
            text: body.purposeText ?? 'Care Management',
            refUri: 'https://www.abdm.gov.in/purpose/' + (body.purpose ?? 'CAREMGT'),
          },
          patient: { id: body.healthId },
          hiu:     { id: '' },   // filled by backend from JWT tenantId
          requester: { name: 'Doctor', identifier: { type: 'REGNO1', value: 'ABDM', system: 'https://www.mciindia.org' } },
          hiTypes: body.hiTypes,
          permission: {
            accessMode: 'VIEW',
            dateRange: { from: body.permissionFrom, to: body.permissionTo },
            dataEraseAt: body.expiry,
            frequency: { unit: 'HOUR', value: 1, repeats: 0 },
          },
        },
      },
    }),

  fhir: (artifactId: string, tenantId: string) =>
    request<any[]>({
      url: 'api/abdm/fhir-bundles/artifact',
      method: 'GET',
      params: { artifactId, tenantId },
    }),

  /** Re-trigger health-information/request for an existing artefact.
   *  Useful when the initial request's on-request callback was quarantined
   *  and no FHIR data was stored. Data appears after ~30-60s. */
  retrigger: (artifactId: string) =>
    request<{ status: string; message: string }>({
      url: 'api/abdm/fhir-bundles/retrigger',
      method: 'POST',
      params: { artifactId },
    }),
};
